<template>
  <div id="app">
    <!-- 设备信息组件（可拖拽组件-->
    <device-info :visible="deviceInfoVisible" :draggable="true" @close="handleCloseDeviceInfo" />
    
    <legacy-style-compat-shell>
      <a-config-provider :locale="locale" :theme="antdTheme">
        <router-view />
        <course-type-select-host />
        <study-plan-set-host />
      </a-config-provider>
    </legacy-style-compat-shell>
    <!-- 备案信息 -->
    <div v-if="showIcpFooter" class="icp-footer">
      <div class="icp-content">
        <a v-if="domainIcp" :href="domainIcpLink" target="_blank" rel="noopener noreferrer" class="icp-link">
          {{ domainIcp }}
        </a>
        <span v-if="domainIcp && policeIcp" class="icp-separator">|</span>
        <a v-if="policeIcp" :href="policeIcpLink" target="_blank" rel="noopener noreferrer" class="icp-link">
          {{ policeIcp }}
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import zhCN from 'ant-design-vue/es/locale/zh_CN'
import { theme as antdTheme } from 'ant-design-vue'
import { mapGetters } from 'vuex'
import DeviceInfo from '@/components/DeviceInfo'
import CourseTypeSelectHost from '@/components/CourseTypeSelectHost.vue'
import StudyPlanSetHost from '@/components/StudyPlanSetHost.vue'
import LegacyStyleCompatShell from '@/components/LegacyStyleCompatShell.vue'

export default {
  name: 'App',
  components: {
    DeviceInfo,
    CourseTypeSelectHost,
    StudyPlanSetHost,
    LegacyStyleCompatShell
  },
  data () {
    return {
      locale: zhCN
    }
  },
  computed: {
    ...mapGetters('common', ['systemConf']),
    deviceInfoVisible () {
      return this.$store.getters['common/deviceInfoVisible']
    },
    themeMode () {
      return this.$store.getters['common/themeMode']
    },
    isDark () {
      if (this.themeMode === 'dark') return true
      if (this.themeMode === 'light') return false
      return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
    },
    antdTheme () {
      return {
        algorithm: this.isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
        token: {
          colorPrimary: '#7c3aed'
        }
      }
    },
    // 域名备案号（工信部）
    domainIcp () {
      return this.systemConf?.domain || ''
    },
    // 公安备案号
    policeIcp () {
      return this.systemConf?.web_site_icp || ''
    },
    // 是否显示备案号区域（至少有一个备案号）
    showIcpFooter () {
      return !!(this.domainIcp || this.policeIcp)
    },
    // 域名备案链接（工信部备案查询）
    domainIcpLink () {
      return 'https://beian.miit.gov.cn/'
    },
    // 公安备案链接（公安部备案查询）
    policeIcpLink () {
      if (!this.policeIcp) {
        return 'http://www.beian.gov.cn/portal/registerSystemInfo'
      }
      // 提取公安备案号中的数字部分（通常格式为：京公网安11010502012345号）
      const recordNumber = this.policeIcp.match(/\d+/)
      if (recordNumber && recordNumber[0]) {
        return `http://www.beian.gov.cn/portal/registerSystemInfo?recordcode=${recordNumber[0]}`
      }
      return 'http://www.beian.gov.cn/portal/registerSystemInfo'
    }
  },
  mounted () {
    this.applyThemeClass()
    this.bindSystemThemeListener()
  },
  beforeUnmount () {
    this.unbindSystemThemeListener()
  },
  watch: {
    themeMode () {
      this.applyThemeClass()
    }
  },
  methods: {
    handleCloseDeviceInfo () {
      this.$store.commit('common/setDeviceInfoVisible', false)
    },
    applyThemeClass () {
      const root = document.documentElement
      if (this.isDark) {
        root.classList.add('dark')
        root.style.colorScheme = 'dark'
      } else {
        root.classList.remove('dark')
        root.style.colorScheme = 'light'
      }
    },
    bindSystemThemeListener () {
      if (!window.matchMedia) return
      this._mql = window.matchMedia('(prefers-color-scheme: dark)')
      this._mqlHandler = () => {
        if (this.themeMode === 'system') this.applyThemeClass()
      }
      if (this._mql.addEventListener) this._mql.addEventListener('change', this._mqlHandler)
      else this._mql.addListener(this._mqlHandler)
    },
    unbindSystemThemeListener () {
      if (!this._mql || !this._mqlHandler) return
      if (this._mql.removeEventListener) this._mql.removeEventListener('change', this._mqlHandler)
      else this._mql.removeListener(this._mqlHandler)
      this._mql = null
      this._mqlHandler = null
    }
  }
}
</script>

<style lang="scss" scoped>
#app {
  width: 100%;
  height: 100vh;
  height: 100dvh;
  overflow: hidden;
  position: relative;
  box-sizing: border-box;
}

:deep(.h-screen){
  height: 100vh;
  height: 100dvh;
}

/* 备案号样式*/
.icp-footer {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 998;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.67);
  border-top: 1px solid #e2e8f0;
  backdrop-filter: blur(12px);
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  padding-bottom: constant(safe-area-inset-bottom);
  padding-bottom: env(safe-area-inset-bottom);
}

.dark .icp-footer {
  background: rgba(15, 23, 42, 0.95);
  border-top-color: #334155;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.2);
}

.icp-content {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 16px;
}

.icp-link {
  font-size: 12px;
  color: #64748b;
  text-decoration: none;
  padding: 4px 8px;
  border-radius: 6px;
  transition: all 0.2s ease;
  white-space: nowrap;
  font-weight: 500;
}

.icp-link:hover {
  color: #7c3aed;
  background: rgba(124, 58, 237, 0.08);
  top: -1px;
}

.dark .icp-link {
  color: #94a3b8;
}

.dark .icp-link:hover {
  color: #a78bfa;
  background: rgba(167, 139, 250, 0.12);
}

.icp-separator {
  color: #cbd5e1;
  font-size: 12px;
  user-select: none;
  margin: 0 4px;
}

.dark .icp-separator {
  color: #475569;
}

/* 移动端适配 */
@media screen and (max-width: 768px) {
  .icp-footer {
    height: auto;
    min-height: 36px;
  }

  .icp-content {
    gap: 6px;
    flex-wrap: wrap;
    justify-content: center;
    padding: 8px 12px;
  }

  .icp-link {
    font-size: 11px;
    padding: 3px 6px;
  }

  .icp-separator {
    font-size: 11px;
    margin: 0 2px;
  }
}
</style>
